// Pure locale resolution — no side effects, no browser globals, so it is safe to
// import from unit tests and from the i18n bootstrap alike.

export const SUPPORTED_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'et', name: 'Eesti' },
  { code: 'ru', name: 'Русский' },
  { code: 'lv', name: 'Latviešu' },
  { code: 'zh', name: '中文' },
  { code: 'th', name: 'ไทย' },
] as const;

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]['code'];

// Timezones that map to one of our non-English locales. Estonia -> Estonian;
// Russia + Belarus -> Russian. Anything not listed falls back to browser
// language, then English. (Crimea/Simferopol intentionally omitted — no
// territorial claim encoded here; those devices fall through to the default.)
const TZ_LOCALE: Record<string, LocaleCode> = {
  'Europe/Tallinn': 'et',
  'Europe/Riga': 'lv',
  'Asia/Shanghai': 'zh',
  'Asia/Urumqi': 'zh',
  'Asia/Hong_Kong': 'zh',
  'Asia/Macau': 'zh',
  'Asia/Bangkok': 'th',
  // Russia
  'Europe/Kaliningrad': 'ru',
  'Europe/Moscow': 'ru',
  'Europe/Kirov': 'ru',
  'Europe/Volgograd': 'ru',
  'Europe/Astrakhan': 'ru',
  'Europe/Saratov': 'ru',
  'Europe/Ulyanovsk': 'ru',
  'Europe/Samara': 'ru',
  'Asia/Yekaterinburg': 'ru',
  'Asia/Omsk': 'ru',
  'Asia/Novosibirsk': 'ru',
  'Asia/Barnaul': 'ru',
  'Asia/Tomsk': 'ru',
  'Asia/Novokuznetsk': 'ru',
  'Asia/Krasnoyarsk': 'ru',
  'Asia/Irkutsk': 'ru',
  'Asia/Chita': 'ru',
  'Asia/Yakutsk': 'ru',
  'Asia/Khandyga': 'ru',
  'Asia/Vladivostok': 'ru',
  'Asia/Ust-Nera': 'ru',
  'Asia/Magadan': 'ru',
  'Asia/Sakhalin': 'ru',
  'Asia/Srednekolymsk': 'ru',
  'Asia/Kamchatka': 'ru',
  'Asia/Anadyr': 'ru',
  // Belarus
  'Europe/Minsk': 'ru',
};

export function isSupported(code: string | null | undefined): code is LocaleCode {
  return SUPPORTED_LOCALES.some((locale) => locale.code === code);
}

function localeFromLanguages(languages: readonly string[]): LocaleCode | undefined {
  for (const lang of languages) {
    const base = lang?.slice(0, 2).toLowerCase();
    if (isSupported(base)) return base;
  }
  return undefined;
}

/**
 * Pure locale resolution (unit-tested). Priority:
 *   1. explicit saved choice
 *   2. location by timezone
 *   3. browser language
 *   4. English
 * Estonia hosts a large Russian-speaking minority, so when the timezone says
 * Estonia but the browser is set to Russian, honor Russian.
 */
export function resolveLocale(opts: {
  saved?: string | null;
  timeZone?: string | undefined;
  languages?: readonly string[];
}): LocaleCode {
  if (isSupported(opts.saved)) return opts.saved;
  const byTz = opts.timeZone ? TZ_LOCALE[opts.timeZone] : undefined;
  const byLang = localeFromLanguages(opts.languages ?? []);
  if (byTz === 'et' && byLang === 'ru') return 'ru';
  return byTz ?? byLang ?? 'en';
}
